import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, Observable, of } from "rxjs";
import { ReminderInterval, ReminderType } from "src/app/models/Enums";
import { environment } from "src/environments/environment";

import ApiVersionConstants from "../constants/api-version-constants";
import { Reminder } from "../models/Reminder";
import { RemindersByCompany } from "../models/RemindersByCompany";
import { User } from "../models/User";
import { ReminderSubscription } from "./../models/ReminderSubscription";
import { AuthService } from "./auth.service";

export const DEFAULT_DAYS_AFTER: number = 7;
export const DEFAULT_DAYS_BEFORE: number = 30;

@Injectable({
  providedIn: "root",
})
export class ReminderService {
  private baseUrl = `${environment.apiConfig.uri}/${ApiVersionConstants.V_2}/reminder`;

  intervals = [
    { key: 1, title: "reminderinterval.daily", value: "Daily" },
    { key: 2, title: "reminderinterval.weekly", value: "Weekly" },
    { key: 3, title: "reminderinterval.monthly", value: "Monthly" },
  ];

  types: ReminderSubscription[] = [
    { key: 1, title: "remindertype.calibration", value: "Calibration" },
    { key: 2, title: "remindertype.service", value: "Service" },
    { key: 16, title: "remindertype.po", value: "PO" },
    { key: 32, title: "remindertype.receive", value: "Receive" },
    { key: 64, title: "remindertype.shipping", value: "Shipping" },
  ];

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  get(companyId) {
    return this.http
      .get<Reminder[]>(this.baseUrl + "/company/" + companyId)
      .pipe(map((results) => results.map((reminder) => new Reminder().deserialize(reminder))));
  }

  getByFamcode() {
    return this.http
      .get<Reminder[]>(this.baseUrl + "/famcodelist")
      .pipe(map((results) => results.map((reminder) => new Reminder().deserialize(reminder))));
  }

  getByCompany(): Observable<RemindersByCompany[]> {
    return this.http.get<RemindersByCompany[]>(this.baseUrl + "/grouped").pipe(
      map((result) => {
        result.forEach(
          (group) =>
            (group.reminders = group.reminders.map((reminder) =>
              new Reminder().deserialize(reminder),
            )),
        );
        return result;
      }),
    );
  }

  create(reminder: Reminder) {
    return this.http
      .post<Reminder>(this.baseUrl, reminder)
      .pipe(map((result) => new Reminder().deserialize(result)));
  }

  bulkCreate(reminders: Reminder[]) {
    return this.http
      .post<Reminder[]>(this.baseUrl + "/bulk", reminders)
      .pipe(map((results) => results.map((reminder) => new Reminder().deserialize(reminder))));
  }

  // TODO: make sure that update method returns the update model.
  // now it returns just null
  update(reminder: Reminder, id: number) {
    return this.http.put(this.baseUrl + "/" + id, reminder);
  }

  delete(id) {
    return this.http.delete(this.baseUrl + "/" + id);
  }

  getEmailsByCompany(companyId: string) {
    return this.http.get<string[]>(this.baseUrl + "/" + companyId + "/emails");
  }

  getReminderTypes() {
    return this.types;
  }

  getReminderIntervals() {
    return this.intervals;
  }

  getReminderSubscriptions(user: User): ReminderSubscription[] {
    const reminderTypes = this.getReminderTypes();
    reminderTypes.map((r) => {
      r.selected = !!user.emailSubscriptions?.find((s) => s.types === r.key);
      r.email = r.selected
        ? user.emailSubscriptions?.find((s) => s.types === r.key).email
        : this.authService.user.email;
    });
    return reminderTypes;
  }

  remindersBoilerPlate = [
    new Reminder(null, ReminderType.Calibration, ReminderInterval.Weekly, DEFAULT_DAYS_BEFORE),
    new Reminder(null, ReminderType.Service, ReminderInterval.Weekly, DEFAULT_DAYS_BEFORE),
    new Reminder(null, ReminderType.PO, ReminderInterval.Weekly, DEFAULT_DAYS_AFTER),
    new Reminder(null, ReminderType.Receive, ReminderInterval.Daily),
    new Reminder(null, ReminderType.Shipping, ReminderInterval.Daily),
  ];

  createDefaults(company: RemindersByCompany, emails: string[]) {
    const defaultReminders = this.remindersBoilerPlate.filter(
      (x) => !company.reminders.some((r) => r.type == x.type),
    );
    defaultReminders.forEach((r) => {
      (r.companyId = company.name), (r.to = r.type == ReminderType.PO ? null : emails.join("; "));
    });

    return this.bulkCreate(defaultReminders);
  }

  getDefaultEmails(companyId: string) {
    return this.authService.user?.isUserGroupProfile
      ? of([this.authService.user.email])
      : this.getEmailsByCompany(companyId);
  }

  ignoreToRecipients(type: ReminderType) {
    return type == ReminderType.PO;
  }
}
